import React, { FC, useEffect, useState } from "react";
import CustomTable from "../../../../components/Tables/CustomTable";
import { MoreVertical } from "lucide-react";
import DropdownMenu from "../../../ui/DropdownMenu";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import {
  fetchAllReturnedOrders,
  setSelectedReturnedOrder,
} from "../../../../redux/features/orderSlice";
import { RootState } from "../../../../redux/app/store";
import moment from "moment";
import { cleanObject, formatNumberWithCommas } from "../../../../helpers";
import { getStatusStyles } from "../util";

interface OrderTableProps {
  search: string;
  startDate: Date | null;
  endDate: Date | null;
}

const ReturnedOrdersTable: FC<OrderTableProps> = ({
  search,
  startDate,
  endDate,
}) => {
  const { loading, returnedOrders } = useAppSelector(
    (state: RootState) => state.orders
  );
  const [returnedPageLimit, setReturnedPageLimit] = useState<number>(10);
  const [returnedPage, setReturnedPage] = useState<number>(0);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const returnedORderColumns = [
    { label: "S/N", field: "id" },
    { label: "Contract", field: "contract_code" },
    // { label: "Order Weight(Kg)", field: "order_weight" },
    { label: "Name of Vendor", field: "vendor_name" },
    { label: "Name of Buyer", field: "buyer_name" },
    // { label: "Pickup Location", field: "pickup_location" },
    { label: "Order Amount", field: "order_amount" },
    { label: "Created Date", field: "created_date" },
    { label: "Order Status", field: "status" },
    { label: "Dispatch Status", field: "dispatch_status" },
    { label: "Order type", field: "order_type" },
    { label: "Action", field: "returned_action" },
  ];

  const returnedDropdownOptions = [
    {
      label: "View order details",
      onClick: (item: any) => {
        navigate(`/returned-order-details/${item?.id}`);
        dispatch(setSelectedReturnedOrder(item));
      },
      returnAll: true,
    },
    // {
    //   label: <div className="text-accent">Approve returned order</div>,
    //   onClick: () => setOpenApprovereturnedModal(true),
    // },
  ];

  const returnedOrderData = returnedOrders?.data?.data?.map(
    (returned: any, index: number) => {
      const isPaid =
        returned?.status === "PAID" || returned?.status === "COMPLETED";
      return {
        id: index + 1,
        contract_id: returned?.contract?.title,
        returned_weight: returned?.contract?.actualWeight ?? "N/A",
        vendor_name: returned?.contract?.vendorId?.fullName ?? "N/A",
        buyer_name: returned?.contract?.buyerId?.fullName ?? "N/A",
        pickup_location: returned?.pickupLocation?.address,
        contract_code: returned?.contract?.contractCode,
        created_date: moment(returned?.createdAt).format("Do MMM YYYY"),
        order_amount: returned?.orderAmount
          ? `₦ ${formatNumberWithCommas(Number(returned?.orderAmount))}`
          : "N/A",
        returned_reason: returned?.question?.returnedReason?.name,
        status: (
          <span
            className={`text-xs px-2 py-1 rounded-md font-semibold ${
              isPaid
                ? "bg-green-50 text-green-500 border border-green-500"
                : returned?.status === "PENDING"
                ? "bg-yellow-50 text-yellow-500 border border-yellow-500"
                : "bg-red-50 text-red-500 border border-red-500"
            }`}
          >
            {returned?.status}
          </span>
        ),
        dispatch_status: (
          <span
            className={`text-xs px-2 py-1 rounded-md font-semibold ${getStatusStyles(
              returned?.dispatchStatus
            )}`}
          >
            {returned?.dispatchStatus}
          </span>
        ),
        order_type: (
          <span
            className={`text-xs px-2 py-1 rounded-md font-semibold 
            bg-gray-50 text-gray-500 border border-gray-500
                `}
          >
            {returned?.orderType}
          </span>
        ),
        returned_action: (
          <DropdownMenu
            options={returnedDropdownOptions}
            icon={<MoreVertical style={{ cursor: "pointer" }} />}
            item={returned}
          />
        ),
      };
    }
  );

  useEffect(() => {
    const params = cleanObject({
      limit: returnedPageLimit,
      page: returnedPage + 1,
      filter: search,
      startDate,
      endDate,
    });
    dispatch(fetchAllReturnedOrders(params));
  }, [dispatch, returnedPage, returnedPageLimit, search, startDate, endDate]);

  return (
    <CustomTable
      data={{
        columns: returnedORderColumns,
        rows: returnedOrderData,
      }}
      noRecordText="No Returned Order Available"
      loading={loading}
      showFooter={true}
      totalPages={returnedOrders?.data?.totalPages}
      itemsPerPage={returnedOrders?.data?.itemsPerPage}
      currentPage={returnedOrders?.data?.currentPage}
      onPageChange={(value: number) => setReturnedPage(value)}
      onLimitChange={(value: number) => setReturnedPageLimit(value)}
    />
  );
};

export default ReturnedOrdersTable;
